import { useState } from 'react';

export const SMS_DEFAULT_COUNTRY = 'US';
export const SMS_MIN_NATIONAL_DIGITS = 7;

export function nationalPhoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function formatResendClock(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const minutes = Math.floor(clamped / 60);
  const remainder = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

export function toE164Phone(dialCode: string, nationalNumber: string): string {
  const dial = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
  return `${dial}${nationalPhoneDigits(nationalNumber)}`;
}

export function maskSmsNumber(dialCode: string, nationalNumber: string): string {
  const last = nationalPhoneDigits(nationalNumber).slice(-3);
  return `${dialCode} ••• ••• ${last}`;
}

export type MfaSmsSetupPhase = 'phone' | 'verify' | 'invalid';

export interface UseMfaSmsSetupFlowParams {
  actionError?: string;
  busy: boolean;
  invalidMessage: string;
  onClearActionError: () => void;
  onSendSmsCode: (phoneE164: string) => Promise<boolean>;
  onSetupCodeChange: (value: string) => void;
  phoneNumberSent?: string;
}

export interface UseMfaSmsSetupFlowResult {
  dialCode: string;
  effectivePhase: MfaSmsSetupPhase;
  goToPhone: () => void;
  masked: string;
  phone: string;
  phoneReady: boolean;
  retryVerify: () => void;
  sendCode: () => void;
  setDialCode: (value: string) => void;
  setPhone: (value: string) => void;
}

/**
 * Owns the phone -> verify step machine for SMS enrollment.
 *
 * The step is derived, not synced: the initial step comes from whether the
 * parent has already sent a code (`phoneNumberSent`, read only once via the
 * lazy `useState` initializer), and every later transition happens inside the
 * event handler that causes it (`sendCode`, `goToPhone`, `retryVerify`) —
 * never in an effect reacting to a prop or to another piece of state. An
 * invalid-code error routes to the "invalid" step for as long as it's live by
 * computing `effectivePhase` during render, so there's nothing to keep in
 * sync and nothing that can go stale between renders.
 */
export function useMfaSmsSetupFlow({
  actionError,
  busy,
  invalidMessage,
  onClearActionError,
  onSendSmsCode,
  onSetupCodeChange,
  phoneNumberSent,
}: UseMfaSmsSetupFlowParams): UseMfaSmsSetupFlowResult {
  const [phase, setPhase] = useState<'phone' | 'verify'>(phoneNumberSent ? 'verify' : 'phone');
  const [dialCode, setDialCode] = useState('+1');
  const [phone, setPhone] = useState('');

  const phoneReady = nationalPhoneDigits(phone).length >= SMS_MIN_NATIONAL_DIGITS;
  const masked =
    phoneNumberSent && phoneNumberSent.length > 3
      ? `${phoneNumberSent.slice(0, phoneNumberSent.length - 3).replace(/\d/g, '•')}${phoneNumberSent.slice(-3)}`
      : maskSmsNumber(dialCode, phone);
  const effectivePhase: MfaSmsSetupPhase = phase === 'verify' && actionError === invalidMessage ? 'invalid' : phase;

  const goToPhone = (): void => {
    onSetupCodeChange('');
    onClearActionError();
    setPhase('phone');
  };

  const retryVerify = (): void => {
    onSetupCodeChange('');
    onClearActionError();
    setPhase('verify');
  };

  const sendCode = (): void => {
    if (!phoneReady || busy) {
      return;
    }
    void (async () => {
      const sent = await onSendSmsCode(toE164Phone(dialCode, phone));
      if (sent) {
        onSetupCodeChange('');
        setPhase('verify');
      }
    })();
  };

  return {
    dialCode,
    effectivePhase,
    goToPhone,
    masked,
    phone,
    phoneReady,
    retryVerify,
    sendCode,
    setDialCode,
    setPhone,
  };
}
